import React from "react"

export type GaugeThreshold = {
  min: number
  max: number
  label: string
  description?: string
  gradientStart: string
  gradientEnd: string
}

type GaugeProps = {
  value: number
  max?: number
  thresholds?: GaugeThreshold[]
  className?: string
  minimal?: boolean
  showValue?: boolean
  valueSuffix?: React.ReactNode
  children?: React.ReactNode
}

const DEFAULT_THRESHOLDS: GaugeThreshold[] = [
  {
    min: 90,
    max: 100,
    label: "excellent",
    gradientStart: "var(--colors-success-600)",
    gradientEnd: "var(--colors-success-400)",
  },
  {
    min: 70,
    max: 89,
    label: "healthy",
    gradientStart: "var(--colors-success-500)",
    gradientEnd: "var(--colors-success-300)",
  },
  {
    min: 50,
    max: 69,
    label: "at-risk",
    gradientStart: "var(--colors-warning-500)",
    gradientEnd: "var(--colors-warning-300)",
  },
  {
    min: 0,
    max: 49,
    label: "critical",
    gradientStart: "var(--colors-danger-600)",
    gradientEnd: "var(--colors-danger-400)",
  },
]

const DEFAULT_MASK_TICKS = [
  { y: "96.4283", width: "21.8182", height: "3.57141", rx: "1.78571" },
  {
    width: "21.8116",
    height: "3.57253",
    rx: "1.78627",
    transform: "matrix(0.991744 0.128234 -0.132858 0.991135 1.09267 83.6241)",
  },
  {
    width: "21.7923",
    height: "3.5758",
    rx: "1.7879",
    transform: "matrix(0.967073 0.254498 -0.263203 0.964741 3.87804 71.0697)",
  },
  {
    width: "21.7616",
    height: "3.581",
    rx: "1.7905",
    transform: "matrix(0.926284 0.376826 -0.3886 0.921407 8.30783 58.9795)",
  },
  {
    width: "21.7214",
    height: "3.58776",
    rx: "1.79388",
    transform: "matrix(0.869883 0.493257 -0.506773 0.862079 14.3066 47.5606)",
  },
  {
    width: "21.6746",
    height: "3.59562",
    rx: "1.79781",
    transform: "matrix(0.798609 0.60185 -0.615659 0.788012 21.7714 37.0087)",
  },
  {
    width: "21.6242",
    height: "3.60404",
    rx: "1.80202",
    transform: "matrix(0.71345 0.700707 -0.71345 0.700707 30.5751 27.5038)",
  },
  {
    width: "21.5737",
    height: "3.61243",
    rx: "1.80622",
    transform: "matrix(0.615659 0.788012 -0.798609 0.60185 40.5664 19.2085)",
  },
  {
    width: "21.5266",
    height: "3.62024",
    rx: "1.81012",
    transform: "matrix(0.506773 0.862079 -0.869883 0.493257 51.5744 12.2655)",
  },
  {
    width: "21.486",
    height: "3.62692",
    rx: "1.81346",
    transform: "matrix(0.3886 0.921407 -0.926284 0.376826 63.412 6.7927)",
  },
  {
    width: "21.4548",
    height: "3.63205",
    rx: "1.81602",
    transform: "matrix(0.263203 0.964741 -0.967073 0.254498 75.8746 2.88429)",
  },
  {
    width: "21.4352",
    height: "3.63527",
    rx: "1.81763",
    transform: "matrix(0.132858 0.991135 -0.991744 0.128234 88.75 0.607445)",
  },
  {
    x: "101.818",
    width: "21.4285",
    height: "3.63636",
    rx: "1.81818",
    transform: "rotate(90 101.818 0)",
  },
  {
    width: "21.4352",
    height: "3.63527",
    rx: "1.81763",
    transform: "matrix(-0.132858 0.991135 -0.991744 -0.128234 114.855 1.07315)",
  },
  {
    width: "21.4548",
    height: "3.63205",
    rx: "1.81602",
    transform: "matrix(-0.263203 0.964741 -0.967073 -0.254498 127.638 3.80877)",
  },
  {
    width: "21.486",
    height: "3.62692",
    rx: "1.81346",
    transform: "matrix(-0.3886 0.921407 -0.926284 -0.376826 139.948 8.15945)",
  },
  {
    width: "21.5266",
    height: "3.62024",
    rx: "1.81012",
    transform: "matrix(-0.506773 0.862079 -0.869883 -0.493257 151.574 14.0511)",
  },
  {
    width: "21.5737",
    height: "3.61243",
    rx: "1.80622",
    transform: "matrix(-0.615659 0.788012 -0.798609 -0.60185 162.319 21.3825)",
  },
  {
    width: "21.6242",
    height: "3.60404",
    rx: "1.80202",
    transform: "matrix(-0.71345 0.700707 -0.71345 -0.700707 171.997 30.029)",
  },
  {
    width: "21.6746",
    height: "3.59562",
    rx: "1.79781",
    transform: "matrix(-0.798609 0.60185 -0.615659 -0.788012 180.442 39.8419)",
  },
  {
    width: "21.7214",
    height: "3.58776",
    rx: "1.79388",
    transform: "matrix(-0.869883 0.493257 -0.506773 -0.862079 187.511 50.6533)",
  },
  {
    width: "21.7616",
    height: "3.581",
    rx: "1.7905",
    transform: "matrix(-0.926284 0.376826 -0.3886 -0.921407 193.084 62.2789)",
  },
  {
    width: "21.7923",
    height: "3.5758",
    rx: "1.7879",
    transform: "matrix(-0.967073 0.254498 -0.263203 -0.964741 197.063 74.5194)",
  },
  {
    width: "21.8116",
    height: "3.57253",
    rx: "1.78627",
    transform: "matrix(-0.991744 0.128234 -0.132858 -0.991135 199.382 87.1648)",
  },
  {
    x: "200",
    y: "99.9995",
    width: "21.8182",
    height: "3.57141",
    rx: "1.78571",
    transform: "rotate(180 200 99.9995)",
  },
]

export function Gauge({
  value,
  max = 100,
  thresholds = DEFAULT_THRESHOLDS,
  className,
  minimal = false,
  showValue = true,
  valueSuffix,
  children,
}: GaugeProps) {
  const getThreshold = (val: number): GaugeThreshold => {
    const threshold = thresholds.find((t) => val >= t.min && val <= t.max)
    return threshold || thresholds[thresholds.length - 1]
  }

  const threshold = getThreshold(value)
  const label = threshold.label

  // Calculate how many rectangles should be visible (0-25 based on value 0-max)
  const totalTicks = 25
  const visibleTicks = Math.round((value / max) * totalTicks)

  const maskId = `health-gauge-mask-${Math.random().toString(36).substr(2, 9)}`
  const gradientId = `health-gauge-gradient-${Math.random().toString(36).substr(2, 9)}`

  return (
    <div
      className={`health-gauge health-gauge--${label} ${minimal ? "health-gauge--minimal" : ""} ${className || ""}`.trim()}
    >
      <style>
        {`
          .health-gauge--${label} {
            --health-gauge-gradient-start: ${threshold.gradientStart};
            --health-gauge-gradient-end: ${threshold.gradientEnd};
          }
        `}
      </style>
      <div className="health-gauge__chart-wrapper">
        <svg
          width="200"
          height="100"
          viewBox="0 0 200 100"
          fill="none"
          preserveAspectRatio="xMinYMin"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id={maskId}>
              {DEFAULT_MASK_TICKS.map((tick, index) => (
                <rect key={index} {...tick} fill={index < visibleTicks ? "white" : "black"} />
              ))}
            </mask>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--health-gauge-gradient-start)" />
              <stop offset="100%" stopColor="var(--health-gauge-gradient-end)" />
            </linearGradient>
          </defs>
          <g>
            <rect x="102" width="21" height="4" rx="2" transform="rotate(90 102 0)" fill="var(--colors-grey-300)" />
            <rect
              width="21.2498"
              height="3.66711"
              rx="1.83355"
              transform="matrix(-0.13523 0.990814 -0.992033 -0.12598 114.884 1.16635)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.2894"
              height="3.66072"
              rx="1.83036"
              transform="matrix(-0.267649 0.963517 -0.968184 -0.25024 127.679 3.89747)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.3522"
              height="3.65055"
              rx="1.82527"
              transform="matrix(-0.394574 0.918864 -0.928621 -0.37103 140.001 8.24213)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.4338"
              height="3.63724"
              rx="1.81862"
              transform="matrix(-0.513574 0.858045 -0.873654 -0.486548 151.638 14.1264)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.5284"
              height="3.62169"
              rx="1.81084"
              transform="matrix(-0.62254 0.782588 -0.803779 -0.594928 162.392 21.4491)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.6295"
              height="3.60492"
              rx="1.80246"
              transform="matrix(-0.719732 0.694253 -0.719732 -0.694253 172.078 30.0857)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.7301"
              height="3.58807"
              rx="1.79404"
              transform="matrix(-0.803779 0.594928 -0.62254 -0.782588 180.531 39.8877)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.8235"
              height="3.5723"
              rx="1.78615"
              transform="matrix(-0.873654 0.486548 -0.513574 -0.858045 187.605 50.6876)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.9033"
              height="3.5587"
              rx="1.77935"
              transform="matrix(-0.928621 0.37103 -0.394574 -0.918864 193.181 62.301)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.9643"
              height="3.54823"
              rx="1.77412"
              transform="matrix(-0.968184 0.25024 -0.267649 -0.963517 197.163 74.529)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="22.0026"
              height="3.54164"
              rx="1.77082"
              transform="matrix(-0.992033 0.12598 -0.13523 -0.990814 199.482 87.1617)"
              fill="var(--colors-grey-300)"
            />
            <rect
              x="200"
              y="100"
              width="22"
              height="4"
              rx="2"
              transform="rotate(180 200 100)"
              fill="var(--colors-grey-300)"
            />
            <rect y="96" width="22" height="4" rx="2" fill="var(--colors-grey-300)" />
            <rect
              width="22.0026"
              height="3.54164"
              rx="1.77082"
              transform="matrix(0.992033 0.12598 -0.13523 0.990814 0.996902 83.6527)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.9643"
              height="3.54823"
              rx="1.77412"
              transform="matrix(0.968184 0.25024 -0.267649 0.963517 3.78691 71.1103)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.9033"
              height="3.5587"
              rx="1.77935"
              transform="matrix(0.928621 0.37103 -0.394574 0.918864 8.2229 59.0312)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.8235"
              height="3.5723"
              rx="1.78615"
              transform="matrix(0.873654 0.486548 -0.513574 0.858045 14.2294 47.6227)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.7301"
              height="3.58807"
              rx="1.79404"
              transform="matrix(0.803779 0.594928 -0.62254 0.782588 21.703 37.08)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.6295"
              height="3.60492"
              rx="1.80246"
              transform="matrix(0.719732 0.694253 -0.719732 0.694253 30.5169 27.5832)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.5284"
              height="3.62169"
              rx="1.81084"
              transform="matrix(0.62254 0.782588 -0.803779 0.594928 40.5193 19.2946)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.4338"
              height="3.63724"
              rx="1.81862"
              transform="matrix(0.513574 0.858045 -0.873654 0.486548 51.5393 12.3568)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.3522"
              height="3.65055"
              rx="1.82527"
              transform="matrix(0.394574 0.918864 -0.928621 0.37103 63.3894 6.88765)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.2894"
              height="3.66072"
              rx="1.83036"
              transform="matrix(0.267649 0.963517 -0.968184 0.25024 75.8649 2.98128)"
              fill="var(--colors-grey-300)"
            />
            <rect
              width="21.2498"
              height="3.66711"
              rx="1.83355"
              transform="matrix(0.13523 0.990814 -0.992033 0.12598 88.7534 0.70483)"
              fill="var(--colors-grey-300)"
            />
          </g>
          <path
            d="M100 0C155.228 0 200 44.7715 200 100H178C178 56.9218 143.078 22 100 22C56.9218 22 22 56.9218 22 100H0C0 44.7715 44.7715 0 100 0Z"
            fill={`url(#${gradientId})`}
            mask={`url(#${maskId})`}
          />
        </svg>

        {showValue && (
          <div className="health-gauge__score">
            {value}
            {valueSuffix}
          </div>
        )}
      </div>
      {children}
    </div>
  )
}
